/* eslint-disable indent */
import React, { useContext, useEffect, useState } from 'react';
import { Grid, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Box } from 'src/components/Box';
import { BoxVariants } from 'src/components/Box/types';
import { Typography } from 'src/components/Typography';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import theme from 'src/theme';
import { Wifi } from '@mui/icons-material';
import { NewJobData } from 'src/api/hooks/useJob.types';
import { DrivingLicenseTypes, EditModes, JobTypes } from '../Job.types';
import { LocalizedData } from 'src/DTO/Role.type';
import { useStoreState } from 'src/stores';
import { ORG_TYPE } from 'src/DTO/OrganizationType.enum';
import { LangContext } from 'src/context/LanguageContext';
import { Button } from 'src/components/Button';
import { CheckMark, Clock, Company, JobIcon as JobIcon, Location, Money } from 'src/assets/svg';
import { useTranslation } from 'react-i18next';

export type MobilePreviewData = {
  name?: string; // CreateRoleForm
  city?: string; // CreateRoleForm
  companyProfile?: string; // CreateRoleForm
  description?: string; // CreateRoleForm
  localizedData?: LocalizedData; // CreateRoleForm
  startNow?: boolean; // CreateJobStep1
  timeSchedule?: string; // CreateJobStep1
  jobType?: JobTypes; // CreateJobStep1
  hungarianSkillsNeeded?: boolean; // CreateJobStep2
  englishSkillsNeeded?: boolean; // CreateJobStep2
  documents?: string; // CreateJobStep2
  extraSkills?: string; // CreateJobStep2
  hourlyGrossWage?: number; // CreateJobForm
  weeklyHours?: number; // CreateJobForm
  bonus?: string; // CreateJobForm
  contactName?: string; // CreateJobStep4
  contactEmail?: string; // CreateJobStep4
  contactPhone?: string; // CreateJobStep4
  drivingLicense?: DrivingLicenseTypes; // CreateJobStep2
};

export const getInitialMobilePreviewData = (job?: NewJobData): MobilePreviewData => {
  return {
    name: job?.role?.name, // localized
    city: job?.role?.city,
    companyProfile: job?.role?.companyProfile, // localized
    description: job?.role?.description, // localized
    localizedData: job?.role?.localizedData?.en,
    startNow: job?.startNow,
    timeSchedule: job?.timeSchedule, // localized
    jobType: job?.jobType,
    hungarianSkillsNeeded: job?.hungarianSkillsNeeded,
    englishSkillsNeeded: job?.englishSkillsNeeded,
    documents: job?.documents, // localized
    extraSkills: job?.extraSkills, // localized
    hourlyGrossWage: job?.hourlyGrossWage,
    weeklyHours: job?.weeklyHours,
    bonus: job?.bonus, // localized
    contactName: job?.contactName,
    contactEmail: job?.contactEmail,
    contactPhone: job?.contactPhone,
    drivingLicense: job?.drivingLicense || DrivingLicenseTypes.None,
  };
};

type MobilePreviewProps = {
  jobData: MobilePreviewData;
  editMode?: EditModes;
};

export const MobilePreview: React.FC<MobilePreviewProps> = ({ jobData, editMode }) => {
  // GLOBAL STATE
  const { companies } = useStoreState(state => state.user.data);
  const { selectedLanguages } = useContext(LangContext);
  const { t } = useTranslation();

  // LOCAL STATE
  const [selectedLang, setSelectedLang] = useState<'hu' | 'en'>(
    !jobData?.name && !!jobData?.localizedData?.name ? 'en' : 'hu',
  );

  // DERIVED STATE
  const localizedData = jobData?.localizedData;

  const canSelectLang =
    (!editMode && selectedLanguages.en && selectedLanguages.hu) ||
    (!!editMode && localizedData?.selectedLanguages?.en && localizedData?.selectedLanguages?.hu);

  const hasJobConditions = [
    selectedLang === 'hu' ? jobData?.documents : localizedData?.documents,
    selectedLang === 'hu' ? jobData?.extraSkills : localizedData?.extraSkills,
    jobData?.hungarianSkillsNeeded,
    jobData?.englishSkillsNeeded,
    jobData?.drivingLicense && jobData.drivingLicense !== DrivingLicenseTypes.None,
  ].some(Boolean);

  const hasContactInfo = Boolean(
    jobData?.contactEmail || jobData?.contactName || jobData?.contactPhone,
  );

  const isCompany = !!companies?.length && companies[0].organizationType === ORG_TYPE.COMPANY;

  const getLocalizedValue = (key: keyof LocalizedData) => {
    return selectedLang === 'hu'
      ? jobData?.[key as keyof MobilePreviewData]
      : localizedData?.[key as keyof LocalizedData];
  };

  useEffect(() => {
    if (!editMode) {
      if (selectedLanguages.en && !selectedLanguages.hu) setSelectedLang('en');
      if (!selectedLanguages.en && selectedLanguages.hu) setSelectedLang('hu');
    }
  }, [selectedLanguages]);

  return (
    <>
      {canSelectLang && (
        <Box mb={2} display="flex" justifyContent="center">
          <ToggleButtonGroup
            exclusive
            color="info"
            onChange={(_, value) => setSelectedLang(value)}
            value={selectedLang}>
            <ToggleButton value="hu" disabled={selectedLang === 'hu'}>
              HU
            </ToggleButton>
            <ToggleButton value="en" disabled={selectedLang === 'en'}>
              EN
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <Box
        width="330px"
        height="650px"
        borderRadius="40px"
        border="8px solid black"
        bgColor={theme.palette.grey[200]}
        sx={{
          overflow: 'auto',
          scrollbarWidth: 'none',
        }}>
        <Box position="sticky" top={0} variant={BoxVariants.MOBILE_APP_GRADIENT} pt={2} pb={1}>
          <Box display="flex" alignItems="center" justifyContent="space-evenly">
            <Typography
              sx={{ flex: 1 }}
              fontSize={16}
              align="center"
              color={TYPOGRAPHY_COLORS.WHITE}>
              11:48
            </Typography>
            <Box height="30px" width="100px" borderRadius="15px" bgColor="black" />
            <Box
              display="flex"
              alignItems="center"
              gap="5px"
              sx={{ flex: 1 }}
              justifyContent="center">
              <Wifi sx={{ color: '#fff' }} />
              <Box width="20px" height="8px" bgColor="white" borderRadius="xs" />
            </Box>
          </Box>
          <Typography
            mt={2}
            color={TYPOGRAPHY_COLORS.WHITE}
            align="center"
            fontSize={14}
            fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('jobPage.mobilePreview.title', { lng: selectedLang })}
          </Typography>
        </Box>

        <Stack p={1} spacing={2} mb={1}>
          <Box borderRadius="20px" bgColor="white" p={2}>
            <Typography
              align="center"
              fontSize={16}
              color={TYPOGRAPHY_COLORS.BLACK}
              fontWeight={TYPOGRAPHY_WEIGHT.BOLD}>
              {getLocalizedValue('name') ||
                t('jobPage.mobilePreview.jobTitle', { lng: selectedLang })}
            </Typography>
            <Grid container mt={1}>
              <Grid item xs={6}>
                <Stack spacing={1}>
                  {!!jobData.hourlyGrossWage && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Money width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {jobData.hourlyGrossWage} Ft
                      </Typography>
                    </Box>
                  )}
                  {!!jobData.weeklyHours && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Clock width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {jobData.weeklyHours}{' '}
                        {t('jobPage.mobilePreview.weeklyHours', { lng: selectedLang })}
                      </Typography>
                    </Box>
                  )}
                  {jobData.startNow !== undefined && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckMark width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {jobData?.startNow
                          ? t('jobPage.mobilePreview.startNow', { lng: selectedLang })
                          : t('jobPage.mobilePreview.startLater', { lng: selectedLang })}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack spacing={1}>
                  {!!jobData.city && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Location width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {jobData.city}
                      </Typography>
                    </Box>
                  )}
                  {!!jobData.jobType && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <JobIcon width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {jobData.jobType === JobTypes.LongtermJob
                          ? t('jobPage.mobilePreview.longTermJob', { lng: selectedLang })
                          : t('jobPage.mobilePreview.shortTermJob', { lng: selectedLang })}
                      </Typography>
                    </Box>
                  )}
                  {!!companies?.length && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Company width={20} height={20} color={theme.palette.primary.dark} />
                      <Typography fontSize={12} color={TYPOGRAPHY_COLORS.BLACK}>
                        {isCompany ? 'HelloStudent' : companies[0].name}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {!!getLocalizedValue('companyProfile') && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.mobilePreview.companyProfile', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                <Typography
                  fontSize={10}
                  color={TYPOGRAPHY_COLORS.BLACK}
                  sx={{ whiteSpace: 'pre-line' }}>
                  {getLocalizedValue('companyProfile')}
                </Typography>
              </Box>
            </Box>
          )}

          {!!getLocalizedValue('description') && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.mobilePreview.jobDescription', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                <Typography
                  fontSize={10}
                  color={TYPOGRAPHY_COLORS.BLACK}
                  sx={{ whiteSpace: 'pre-line' }}>
                  {getLocalizedValue('description')}
                </Typography>
              </Box>
            </Box>
          )}

          {hasJobConditions && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.mobilePreview.neededForJob', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                {!!getLocalizedValue('documents') && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    {getLocalizedValue('documents')}
                  </Typography>
                )}
                {!!jobData.hungarianSkillsNeeded && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    {t('jobPage.mobilePreview.hungarianSkills', { lng: selectedLang })}
                  </Typography>
                )}
                {!!jobData.englishSkillsNeeded && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    {t('jobPage.mobilePreview.englishSkills', { lng: selectedLang })}
                  </Typography>
                )}
                {!!getLocalizedValue('extraSkills') && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    {getLocalizedValue('extraSkills')}
                  </Typography>
                )}
                {jobData.drivingLicense !== DrivingLicenseTypes.None && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    {jobData.drivingLicense === DrivingLicenseTypes.Car
                      ? t('jobPage.mobilePreview.carDrivingLicense', { lng: selectedLang })
                      : t('jobPage.mobilePreview.motorcycleDrivingLicense', { lng: selectedLang })}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {!!getLocalizedValue('timeSchedule') && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.mobilePreview.workingHours', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK}>
                  {getLocalizedValue('timeSchedule')}
                </Typography>
              </Box>
            </Box>
          )}

          {!!getLocalizedValue('bonus') && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.createJobForm.bonus', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK}>
                  {getLocalizedValue('bonus')}
                </Typography>
              </Box>
            </Box>
          )}

          {!!jobData.hourlyGrossWage && !!jobData.weeklyHours && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Typography
                variant="caption"
                fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                color={TYPOGRAPHY_COLORS.BLACK}>
                {t('jobPage.mobilePreview.monthlyWage', { lng: selectedLang })}
              </Typography>
              <Box display="flex" justifyContent="center" mt={2} mb={2}>
                <Box
                  width="120px"
                  height="120px"
                  borderRadius="50%"
                  border="10px solid orange"
                  display="flex"
                  alignItems="center"
                  justifyContent="center">
                  <Typography
                    fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                    color={TYPOGRAPHY_COLORS.BLACK}
                    fontSize={16}>
                    {`${(jobData.weeklyHours * 4 * jobData.hourlyGrossWage)
                      .toLocaleString('hu-HU')
                      .replace(/\s/g, ' ')} Ft`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {!isCompany && hasContactInfo && (
            <Box borderRadius="20px" bgColor="white" p={2}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                  color={TYPOGRAPHY_COLORS.BLACK}>
                  {t('jobPage.createJobForm.contact.info', { lng: selectedLang })}
                </Typography>
              </Box>
              <Box>
                {jobData.contactName && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    <Typography
                      fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                      component="span"
                      fontSize={10}
                      color={TYPOGRAPHY_COLORS.BLACK}>
                      {t('jobPage.createJobForm.contact.name', { lng: selectedLang })}
                    </Typography>
                    : {jobData?.contactName}
                  </Typography>
                )}
                {jobData.contactPhone && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    <Typography
                      fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                      component="span"
                      fontSize={10}
                      color={TYPOGRAPHY_COLORS.BLACK}>
                      {t('jobPage.createJobForm.contact.phone', { lng: selectedLang })}
                    </Typography>
                    : {jobData?.contactPhone}
                  </Typography>
                )}
                {jobData.contactEmail && (
                  <Typography fontSize={10} color={TYPOGRAPHY_COLORS.BLACK} display="block">
                    <Typography
                      fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                      component="span"
                      fontSize={10}
                      color={TYPOGRAPHY_COLORS.BLACK}>
                      {t('jobPage.createJobForm.contact.email', { lng: selectedLang })}
                    </Typography>
                    : {jobData?.contactEmail}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Box borderRadius="20px" bgColor="orange" p={2}>
            <Typography
              variant="caption"
              fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
              color={TYPOGRAPHY_COLORS.WHITE}>
              {isCompany
                ? t('jobPage.mobilePreview.haveQuestionsAboutJob', { lng: selectedLang })
                : t('jobPage.mobilePreview.haveQuestionsAboutApp', { lng: selectedLang })}
            </Typography>
            <Box display="flex" justifyContent="center" mt={1}>
              <Button
                fullWidth
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.fontWeightLight,
                  fontSize: 10,
                }}>
                {t('jobPage.mobilePreview.openChat', { lng: selectedLang })}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Box>
    </>
  );
};
